import { useEffect, useRef } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from '@iwoca/orion';
import { FormProvider, useForm } from 'react-hook-form';

import { businessTypeValidator } from './businessTypeValidator';
import { SoleTraderOption } from './SoleTraderOption/SoleTraderOption';
import logger from '../../../../utils/logger';
import { InlineButton } from '../../../components/InlineButton/InlineButton';
import { WhiteLabelOverride } from '../../../components/WhiteLabelOverride/WhiteLabelOverride';
import { useSoleTraderPilot } from '../../../hooks/useSoletraderPilot';
import { HookFormRadioButton } from '../../components/RadioButton/RadioButton';
import { StepCard } from '../../components/StepCard/StepCard';
import { BUSINESS_DETAILS_STEP_ID, COMPANY_TYPE_STEP_ID } from '../Steps';
import { useNavigateToStep } from '../useNavigateToStep';
import { useStepData } from '../useStepData';
import { useStepStatus } from '../useStepStatus';

export const BUSINESS_TYPE_OPTIONS = [
  {
    label: 'Limited Company',
    value: 'limited_liability_company',
  },
  {
    label: 'Limited Liability Partnership',
    value: 'limited_liability_partnership',
  },
  { label: 'Sole Trader', value: 'sole_trader' },
] as const;

export const BusinessType = () => {
  const { stepData, updateStepData, isUpdatingStepData } =
    useStepData(COMPANY_TYPE_STEP_ID);
  const stepStatus = useStepStatus(COMPANY_TYPE_STEP_ID);
  const isActive = stepStatus === 'active';
  const isCompleted = stepStatus === 'complete';

  const goToCurrentStep = useNavigateToStep(COMPANY_TYPE_STEP_ID);
  const goToNextStep = useNavigateToStep(BUSINESS_DETAILS_STEP_ID);

  const methods = useForm({
    defaultValues: stepData,
    resolver: yupResolver(businessTypeValidator),
  });

  const companyType = methods.watch('companyType');
  const isSoleTrader = companyType === 'sole_trader';
  const { eligible: isSoleTraderPilot, acceptingNewApprovals } =
    useSoleTraderPilot();

  const showSoleTraderPayNowButton =
    isSoleTrader && (!acceptingNewApprovals || !isSoleTraderPilot);

  const handleSubmit = async (values: typeof stepData) => {
    await updateStepData(values);
    goToNextStep();
  };

  // The pay-now CTA replaces this step's submit button, so nothing else writes
  // the business type. The SoleTrader record it creates is what excludes
  // blocked sole traders from the seller's customers dashboard.
  const hasPersistedSoleTrader = useRef(false);
  useEffect(() => {
    if (!showSoleTraderPayNowButton) {
      hasPersistedSoleTrader.current = false;
      return;
    }
    if (hasPersistedSoleTrader.current) return;
    if (stepData?.companyType === 'sole_trader') return;

    hasPersistedSoleTrader.current = true;
    updateStepData({ companyType: 'sole_trader' }).catch((error) => {
      logger.error('Failed to persist sole trader business type', { error });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSoleTraderPayNowButton, stepData?.companyType]);

  const onSubmit = methods.handleSubmit(handleSubmit);

  return (
    <StepCard title="1. Business type" isCompleted={isCompleted}>
      <FormProvider {...methods}>
        {isActive ? (
          <form onSubmit={onSubmit}>
            <HookFormRadioButton
              labelText="What type of business are you?"
              name="companyType"
              options={BUSINESS_TYPE_OPTIONS}
              className="min-w-[500px] xs:min-w-0"
            />
            {showSoleTraderPayNowButton ? (
              <SoleTraderOption loading={isUpdatingStepData} />
            ) : (
              <WhiteLabelOverride
                componentType="button"
                disabled={isUpdatingStepData}
              >
                <Button
                  disabled={isUpdatingStepData}
                  loading={isUpdatingStepData}
                  className="mt-m sm:w-full"
                >
                  Continue to business info
                </Button>
              </WhiteLabelOverride>
            )}
          </form>
        ) : (
          <div>
            <div className="mb-l font-reg">
              {
                BUSINESS_TYPE_OPTIONS.find(({ value }) => value === companyType)
                  ?.label
              }
            </div>
            <InlineButton onClick={goToCurrentStep}>Edit</InlineButton>
          </div>
        )}
      </FormProvider>
    </StepCard>
  );
};
